import { ATHENA_EVENT_TYPES } from './AthenaEventBus';

/**
 * The Event Engine is deliberately a classifier, not an executor. Native and
 * server side effects must still pass through the Action Router or the Worker.
 * Keeping it pure makes device signals harmless by default.
 */
export const ATHENA_EVENT_DECISIONS = Object.freeze({
  CONTEXT_ONLY: 'CONTEXT_ONLY',
  ACTION: 'ACTION',
  PROACTIVE_CANDIDATE: 'PROACTIVE_CANDIDATE',
  IGNORE: 'IGNORE',
});

const MAX_DEPTH = 3;
const MAX_TRACKED_KEYS = 240;
const DEFAULT_COOLDOWN_MS = 90 * 1000;

const ACTION_EVENT_TYPES = new Set([
  ATHENA_EVENT_TYPES.NOTIFICATION_ACTION,
  ATHENA_EVENT_TYPES.ALARM_EVENT,
  ATHENA_EVENT_TYPES.NATIVE_INTENT_RECEIVED,
  ATHENA_EVENT_TYPES.SHORTCUT_USED,
  ATHENA_EVENT_TYPES.WIDGET_ACTION,
  ATHENA_EVENT_TYPES.TILE_ACTION,
  ATHENA_EVENT_TYPES.QUICK_CAPTURE_SUBMITTED,
]);

const CONTEXT_EVENT_TYPES = new Set([
  ATHENA_EVENT_TYPES.APP_FOREGROUND,
  ATHENA_EVENT_TYPES.APP_BACKGROUND,
  ATHENA_EVENT_TYPES.NETWORK_CHANGED,
  ATHENA_EVENT_TYPES.NOTIFICATION_RECEIVED,
  ATHENA_EVENT_TYPES.PUSH_TOKEN_REFRESH,
  ATHENA_EVENT_TYPES.SHARE_RECEIVED,
  ATHENA_EVENT_TYPES.DEVICE_CONTEXT_CHANGED,
  ATHENA_EVENT_TYPES.OTA_CHECK,
]);

function safeTimestamp(value) {
  const timestamp = Date.parse(value || '');
  return Number.isFinite(timestamp) ? timestamp : Date.now();
}

function safeKey(value) {
  return typeof value === 'string' && /^[A-Za-z0-9._:-]{1,220}$/.test(value) ? value : '';
}

function actionFor(event) {
  if (event.type === ATHENA_EVENT_TYPES.QUICK_CAPTURE_SUBMITTED) return 'SAVE_QUICK_CAPTURE';
  if (event.type === ATHENA_EVENT_TYPES.NOTIFICATION_ACTION || event.type === ATHENA_EVENT_TYPES.ALARM_EVENT) return 'OPEN_NOTIFICATION_CONTEXT';
  if (event.type === ATHENA_EVENT_TYPES.NATIVE_INTENT_RECEIVED
    || event.type === ATHENA_EVENT_TYPES.SHORTCUT_USED
    || event.type === ATHENA_EVENT_TYPES.WIDGET_ACTION
    || event.type === ATHENA_EVENT_TYPES.TILE_ACTION) return 'HANDLE_INTENT';
  return null;
}

function decision(event, classification, reason, actionType = null) {
  return Object.freeze({
    eventId: event?.id || null,
    classification,
    reason,
    actionType,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Stateful only for bounded idempotency/cooldown bookkeeping. It never stores
 * message bodies or external payloads.
 */
export function createAthenaEventEngine({ cooldownMs = DEFAULT_COOLDOWN_MS } = {}) {
  const seen = new Map();
  const cooldowns = new Map();
  const safeCooldownMs = Number.isFinite(Number(cooldownMs))
    ? Math.max(1_000, Math.min(Number(cooldownMs), 60 * 60 * 1000))
    : DEFAULT_COOLDOWN_MS;

  const prune = (now) => {
    for (const [key, timestamp] of seen) if (now - timestamp > 10 * 60 * 1000) seen.delete(key);
    for (const [key, timestamp] of cooldowns) if (now - timestamp > safeCooldownMs) cooldowns.delete(key);
    while (seen.size > MAX_TRACKED_KEYS) seen.delete(seen.keys().next().value);
    while (cooldowns.size > MAX_TRACKED_KEYS) cooldowns.delete(cooldowns.keys().next().value);
  };

  const classify = (event, { allowProactiveCandidate = false } = {}) => {
    if (!event || typeof event !== 'object') return decision(event, ATHENA_EVENT_DECISIONS.IGNORE, 'invalid_event');
    if (!event.id || !event.type || !event.source) return decision(event, ATHENA_EVENT_DECISIONS.IGNORE, 'invalid_envelope');
    if (!Number.isInteger(event.depth) || event.depth < 0 || event.depth > MAX_DEPTH) {
      return decision(event, ATHENA_EVENT_DECISIONS.IGNORE, 'depth_limit');
    }

    const now = safeTimestamp(event.timestamp);
    prune(now);
    const idempotencyKey = safeKey(event.idempotencyKey) || safeKey(event.id);
    if (idempotencyKey && seen.has(idempotencyKey)) return decision(event, ATHENA_EVENT_DECISIONS.IGNORE, 'duplicate');
    if (idempotencyKey) seen.set(idempotencyKey, now);

    const cooldownKey = `${event.source}:${event.type}:${event.metadata?.intent || event.metadata?.notificationId || ''}`;
    const lastRun = cooldowns.get(cooldownKey);
    if (lastRun && now - lastRun < safeCooldownMs && event.type !== ATHENA_EVENT_TYPES.ALARM_EVENT) {
      return decision(event, ATHENA_EVENT_DECISIONS.IGNORE, 'cooldown');
    }

    if (ACTION_EVENT_TYPES.has(event.type)) {
      cooldowns.set(cooldownKey, now);
      return decision(event, ATHENA_EVENT_DECISIONS.ACTION, 'explicit_or_system_action', actionFor(event));
    }

    if (event.type === ATHENA_EVENT_TYPES.AUTOMATION_TRIGGERED) {
      // The Worker/proactive engine remains responsible for deciding whether a
      // candidate becomes visible. The client only labels the event.
      return decision(
        event,
        allowProactiveCandidate ? ATHENA_EVENT_DECISIONS.PROACTIVE_CANDIDATE : ATHENA_EVENT_DECISIONS.CONTEXT_ONLY,
        allowProactiveCandidate ? 'automation_candidate' : 'automation_feature_disabled',
      );
    }

    if (CONTEXT_EVENT_TYPES.has(event.type)) return decision(event, ATHENA_EVENT_DECISIONS.CONTEXT_ONLY, 'context_signal');
    return decision(event, ATHENA_EVENT_DECISIONS.IGNORE, 'unsupported_event_type');
  };

  return Object.freeze({
    classify,
    clear() {
      seen.clear();
      cooldowns.clear();
    },
  });
}

export const athenaEventEngine = createAthenaEventEngine();

